import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    Input,
    OnInit,
    Renderer2,
    ViewEncapsulation,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DeviceService, IconFastCoreService } from '@frontend/vanilla/core';
import { FastSvgComponent } from '@push-based/ngx-fast-svg';
import { firstValueFrom } from 'rxjs';

import { IconFastService } from './icon-fast.service';
import { IconsetConfig } from './icons.client-config';

/**
 * <vn-icon [name]="dynamicName" />
 * @input `name` is required and should be the name of the icon in site core.
 * @input `size` if provided then width and height not required.
 * @input `width` if provided then height is required.
 * @input `height` if provided then width is required.
 * @input `extraClass` if provided as input then it will apply to svg otherwise extraClass will be taken from sitecore item parameters if available.
 *
 */
@Component({
    imports: [FastSvgComponent],
    selector: 'vn-icon',
    templateUrl: './icon-fast.html',
    host: {
        '[style.height]': 'hostHeight()',
        '[style.width]': 'hostWidth()',
    },
    styles: ['vn-icon {display: inline-flex;} .fast-svg {margin: 0!important; height: auto; width: auto;}'],
    encapsulation: ViewEncapsulation.None,
})
export class IconCustomComponent implements OnInit, AfterViewInit {
    @Input({ required: true }) name: string;
    @Input() size: string;
    @Input() width: string;
    @Input() height: string;
    @Input() extraClass: string;

    readonly deviceService = inject(DeviceService);
    private readonly el = inject(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly iconFastCoreService = inject(IconFastCoreService);
    private readonly iconFastService = inject(IconFastService);
    private readonly iconsConfig = inject(IconsetConfig);
    private readonly destroyRef = inject(DestroyRef);
    private sizeSignal = signal<string | undefined>(undefined);
    private widthSignal = signal<string | undefined>(undefined);
    private heightSignal = signal<string | undefined>(undefined);
    hostHeight = computed(() => {
        const h = this.heightSignal();
        const s = this.sizeSignal();
        return h ? `var(--vn-icon-size, var(--vn-icon-height, ${h}px))` : s ? `var(--vn-icon-size, var(--vn-icon-height, ${s}px))` : null;
    });

    hostWidth = computed(() => {
        const w = this.widthSignal();
        const s = this.sizeSignal();
        return w ? `var(--vn-icon-size, var(--vn-icon-width, ${w}px))` : s ? `var(--vn-icon-size, var(--vn-icon-width, ${s}px))` : null;
    });
    async ngOnInit(): Promise<void> {
        await firstValueFrom(this.iconsConfig.whenReady);
        this.iconFastCoreService.set(this.iconFastService);

        const hostElement: HTMLElement = this.el.nativeElement;
        this.name ??= hostElement.getAttribute('name') ?? hostElement.getAttribute('text') ?? 'not defined';

        if (this.size === undefined) {
            const parentSize = hostElement.getAttribute('size') ?? '24';
            this.size = this.iconFastService.getIconParameter(this.name, 'size') ?? parentSize;
        }

        if (!this.extraClass) {
            this.extraClass = this.iconFastService.getIconParameter(this.name, 'extraClass') ?? '';
        }

        this.sizeSignal.set(this.size);
        this.widthSignal.set(this.width);
        this.heightSignal.set(this.height);
    }

    ngAfterViewInit() {
        const svgElement: HTMLElement = this.el.nativeElement.querySelector('svg');

        if (svgElement) {
            this.renderer.setAttribute(svgElement, 'role', 'img');

            this.iconsConfig.whenReady.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                const fillColor = this.iconFastService.getIconParameter(this.name, 'fillColor');
                if (fillColor) {
                    this.renderer.setAttribute(svgElement, 'fill', fillColor);
                }

                const iconTitle = this.iconFastService.getIconParameter(this.name, 'title');
                if (iconTitle) {
                    this.renderer.setAttribute(svgElement, 'title', iconTitle);
                }
            });
        }
    }
}
